/**
 * Formatos devolvidos pela API.
 *
 * Escritos à mão, e não gerados: são poucos e estáveis, e o gerador traria uma
 * etapa de build que o resto do projeto não tem. Os nomes seguem o banco
 * (`ticket_category`, `product_name`) porque a API devolve a linha como ela é —
 * renomear no meio do caminho só criaria dois vocabulários para a mesma coisa.
 */

export type TicketCategory = 'LOW' | 'MID' | 'HIGH';

export interface Signal {
  id: string;
  label: string;
  weight: number;
  detect?: { field: string; op: string; value?: string | number | string[] };
  hint?: string;
  /** Vale para quase qualquer empresa: pesa, mas não sustenta cross-sell sozinho. */
  generic?: boolean;
}

export interface ProductConfig {
  targetProfiles: string[];
  targetNiches: string[];
  idealCompanySize?: { label: string; minEmployees?: number; maxEmployees?: number };
  problems: string[];
  signals: Signal[];
  qualificationRules: {
    requireContact: boolean;
    requireCity: boolean;
    excludeIfClosed: boolean;
    minReviewCount?: number;
    minRating?: number;
    allowedCategories: string[];
    blockedCategories: string[];
  };
  scoreWeights: { signals: number; presence: number; reach: number };
  salesArguments: string[];
  discoveryQuestions: string[];
  /** Manual de vendas (29/09/2026). Opcionais: produto gravado antes não os tem. */
  pitches?: string[];
  objections?: { when: string; answer: string }[];
  neverPromise?: string[];
  followupAngle?: string;
  analyst: string;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  category: string;
  ticket_category: TicketCategory;
  config: ProductConfig;
  active: boolean;
  sort_order: number;
}

/** O que o painel manda para criar ou editar um produto (camelCase, como a API valida). */
export interface ProductInput {
  name: string;
  description: string;
  category: string;
  ticketCategory: TicketCategory;
  active?: boolean;
  config: ProductConfig;
}

export interface ProductUsage {
  campaigns: number;
  prospects: number;
  scores: number;
  analyses: number;
  messages: number;
}

export interface ProductMeta {
  fields: { prospect: string[]; site: string[]; instagram: string[]; google?: string[]; company?: string[] };
  ops: string[];
  analysts: string[];
}

// --- Equipe --------------------------------------------------------------------

export interface MemberStats {
  userId: string;
  prospectsAdded: number;
  messagesGenerated: number;
  messagesApproved: number;
  messagesSent: number;
  replies: number;
  meetings: number;
  proposals: number;
  won: number;
  notes: number;
  discoveries: number;
  followUpsScheduled: number;
  logins: number;
  lastActivityAt: string | null;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'member';
  last_login_at: string | null;
  created_at: string;
  deleted_at: string | null;
  stats?: MemberStats | null;
}

export interface MemberActivity {
  id: string;
  type: string;
  data: Record<string, unknown>;
  prospect_id: string | null;
  prospect_name: string | null;
  created_at: string;
}

export interface Region {
  city: string;
  state?: string;
  country: string;
}

export type CampaignStatus = 'draft' | 'running' | 'paused' | 'done' | 'failed';

export interface Campaign {
  id: string;
  name: string;
  target_product_id: string;
  niches: string[];
  regions: Region[];
  sources: string[];
  desired_count: number;
  status: CampaignStatus;
  /** Ajustes da campanha que sobrepõem o `.env`. Ausente = vale o padrão. */
  config?: { aiTopN?: number; sendHours?: string; autoAnalyze?: boolean; fullCollection?: boolean; [key: string]: unknown };
  created_at: string;
}

export interface CampaignListItem extends Campaign {
  product_name: string;
  runs: number;
  imported: number;
  /** Empresas que a campanha tem agora (sem as excluídas). */
  prospects: number;
  /** Último lote de leads excluído que ainda pode voltar. */
  restorable: LeadBatch | null;
}

export interface LeadBatch {
  batchId: string;
  count: number;
  deletedAt: string;
}

export interface CampaignRun {
  id: string;
  source: string;
  status: string;
  discovered: number;
  imported: number;
  rejected: number;
  cost_usd: string | null;
  error: string | null;
  created_at: string;
}

export const TICKET_LABEL: Record<TicketCategory, string> = {
  LOW: 'Ticket baixo',
  MID: 'Ticket médio',
  HIGH: 'Ticket alto',
};

export const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: 'Rascunho',
  running: 'Coletando',
  paused: 'Pausada',
  done: 'Concluída',
  failed: 'Com falha',
};

// --- Prospects --------------------------------------------------------------

export const PROSPECT_STATUSES = [
  'NEW',
  'ANALYZING',
  'QUALIFIED',
  'DISQUALIFIED',
  'READY_TO_CONTACT',
  'CONTACTED',
  'REPLIED',
  'MEETING',
  'PROPOSAL',
  'WON',
  'LOST',
  'FOLLOW_UP',
] as const;
export type ProspectStatus = (typeof PROSPECT_STATUSES)[number];

/**
 * Etapas da venda — o espelho de `PROSPECT_STAGES` do core. A etapa agrupa os
 * status por quem deve o próximo passo: eu (a contatar), a empresa
 * (aguardando), eu de novo (responderam).
 */
export const PROSPECT_STAGES = {
  a_contatar: ['NEW', 'ANALYZING', 'QUALIFIED', 'READY_TO_CONTACT'],
  aguardando: ['CONTACTED', 'FOLLOW_UP'],
  responderam: ['REPLIED'],
  negociacao: ['MEETING', 'PROPOSAL'],
  encerrados: ['WON', 'LOST'],
  descartados: ['DISQUALIFIED'],
} as const satisfies Record<string, readonly ProspectStatus[]>;
export type ProspectStage = keyof typeof PROSPECT_STAGES;
export const STAGE_KEYS = Object.keys(PROSPECT_STAGES) as ProspectStage[];

export const STAGE_LABEL: Record<ProspectStage, string> = {
  a_contatar: 'A contatar',
  aguardando: 'Aguardando resposta',
  responderam: 'Responderam',
  negociacao: 'Em negociação',
  encerrados: 'Encerrados',
  descartados: 'Descartados',
};

/** O que cada etapa pede de quem vende — a linha de apoio sob o trilho. */
export const STAGE_HINT: Record<ProspectStage, string> = {
  a_contatar: 'Ainda não receberam mensagem. Comece pelos de maior nota.',
  aguardando: 'Receberam a abordagem e não responderam. Quem espera há mais tempo vem primeiro.',
  responderam: 'A empresa falou. A próxima mensagem é sua.',
  negociacao: 'Reunião marcada ou proposta na mesa.',
  encerrados: 'Vendas fechadas e perdidas.',
  descartados: 'Não passaram na pré-qualificação.',
};

export function stageOf(status: ProspectStatus): ProspectStage {
  return STAGE_KEYS.find((stage) => (PROSPECT_STAGES[stage] as readonly string[]).includes(status))!;
}

export type Priority = 'low' | 'medium' | 'high';

export interface Prospect {
  id: string;
  campaign_id: string | null;
  name: string;
  category: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  phone: string | null;
  website: string | null;
  instagram: string | null;
  google_url: string | null;
  rating: string | null;
  review_count: number | null;
  description: string | null;
  normalized_name: string;
  phone_e164: string | null;
  website_host: string | null;
  instagram_username: string | null;
  status: ProspectStatus;
  priority: Priority;
  disqualified_reason: string | null;
  technical_score: number | null;
  ai_score: number | null;
  final_score: number | null;
  confidence: string | null;
  recommended_product_id: string | null;
  target_product_id?: string | null;
  last_analyzed_at: string | null;
  /** Próxima retomada de contato, "AAAA-MM-DD". */
  follow_up_at?: string | null;
  follow_up_note?: string | null;
  /** Só dígitos; digitado ou confirmado na Receita. */
  cnpj?: string | null;
  created_at: string;
  updated_at: string;
  /** Só na listagem: último envio pelo WhatsApp e última resposta humana. */
  last_contacted_at?: string | null;
  last_reply_at?: string | null;
}

export interface FollowUpItem {
  id: string;
  name: string;
  city: string | null;
  status: ProspectStatus;
  phone_e164: string | null;
  follow_up_at: string;
  follow_up_note: string | null;
  /** 0 = hoje; positivo = dias de atraso; negativo = ainda vai chegar. */
  days_overdue: number;
}

/** Números da operação, informados pelo dono. Dinheiro em centavos. */
export interface DiscoveryNumbers {
  ticketCents?: number;
  appCommissionPct?: number;
  appOrdersPerMonth?: number;
  peakOrders?: number;
}

export interface DiscoveryOffer {
  setupCents?: number;
  monthlyCents?: number;
  notes?: string;
}

export interface DiscoveryInput {
  productId: string | null;
  answers: { question: string; answer: string }[];
  numbers: DiscoveryNumbers;
  offer: DiscoveryOffer;
}

export interface Discovery {
  prospect_id: string;
  product_id: string | null;
  answers: { question: string; answer: string }[];
  numbers: DiscoveryNumbers;
  offer: DiscoveryOffer;
  updated_by_name: string | null;
  updated_at: string;
}

export interface DiscoveryMath {
  commissionPerOrderCents?: number;
  commissionPerMonthCents?: number;
  ordersToPayMonthly?: number;
}

export interface DiscoveryResponse {
  discovery: Discovery | null;
  productId: string | null;
  products: { id: string; name: string; questions: string[] }[];
  questions: string[];
  math: DiscoveryMath;
}

export interface Proposal {
  company: { name: string; signer: string };
  prospect: { name: string; city: string | null; state: string | null };
  product: { name: string; description: string; salesArguments: string[] } | null;
  discovery: Discovery;
  math: DiscoveryMath;
}

export interface ProspectSource {
  id: string;
  source: string;
  source_id: string | null;
  raw: Record<string, unknown>;
  collected_at: string;
}

export interface Activity {
  id: string;
  user_id: string | null;
  prospect_id: string | null;
  type: string;
  data: Record<string, unknown>;
  created_at: string;
}

export interface ProspectList {
  prospects: Prospect[];
  total: number;
  limit: number;
  offset: number;
}

/** Resposta da importação — o mesmo formato com e sem `dryRun`. */
export interface ImportResult {
  dryRun: boolean;
  runId?: string;
  mapped: string[];
  ignored: string[];
  total: number;
  /** Só na pré-visualização. */
  valid?: number;
  preview?: { name: string; city?: string; phoneE164?: string; notes: string[] }[];
  imported?: number;
  duplicates?: number;
  errors: { line: number; error: string }[];
  duplicatesByKey?: Record<string, number>;
}

export const PROSPECT_STATUS_LABEL: Record<ProspectStatus, string> = {
  NEW: 'Novo',
  ANALYZING: 'Em análise',
  QUALIFIED: 'Qualificado',
  DISQUALIFIED: 'Desqualificado',
  READY_TO_CONTACT: 'Pronto para contato',
  CONTACTED: 'Contatado',
  REPLIED: 'Respondeu',
  MEETING: 'Reunião',
  PROPOSAL: 'Proposta',
  WON: 'Ganho',
  LOST: 'Perdido',
  FOLLOW_UP: 'Follow-up',
};

export const PRIORITY_LABEL: Record<Priority, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
};

/**
 * Por que cada duplicata foi considerada a mesma empresa.
 *
 * A tela de importação mostra isto: "12 duplicadas" sem o motivo faz o usuário
 * desconfiar que o sistema comeu os leads dele.
 */
export const MATCH_KEY_LABEL: Record<string, string> = {
  source: 'mesmo id na fonte',
  phone: 'mesmo telefone',
  website: 'mesmo site',
  instagram: 'mesmo Instagram',
  name_city: 'mesmo nome e cidade',
};

// --- Qualificação (Fase 5) --------------------------------------------------

export type EvidenceKind = 'fato' | 'inferencia';

export interface ScoreItem {
  id: string;
  label: string;
  weight: number;
  matched: boolean;
  kind: EvidenceKind;
  evidence: string;
}

export interface DimensionScore {
  value?: number;
  weight: number;
  items: ScoreItem[];
  absent?: string;
}

export interface TechnicalScore {
  score: number;
  /** Quanto do peso total pôde ser medido (0 a 1). */
  coverage: number;
  dimensions: { signals: DimensionScore; presence: DimensionScore; reach: DimensionScore };
  absences: string[];
}

export interface ProspectScore {
  id: string;
  product_id: string;
  /** Nome legível do produto — o `product_id` é slug e não vai para a tela. */
  product_name: string;
  technical_score: number;
  ai_score: number | null;
  final_score: number | null;
  confidence: string | null;
  coverage: string;
  breakdown: TechnicalScore;
  computed_at: string;
}

/** A leitura mais recente da IA para um produto (resumo e nota). */
export interface ProductAnalysis {
  id: string;
  product_id: string;
  product_name: string;
  status: 'ok' | 'failed';
  output: { summary: string; fit: string } | null;
  ai_score: number | null;
  created_at: string;
}

export interface Enrichment {
  id: string;
  kind: 'website' | 'instagram' | 'google' | 'company' | 'pagespeed';
  data: Record<string, unknown>;
  error: string | null;
  fetched_at: string;
}

// --- Presença digital: o que cada leitura apurou ------------------------------

export interface SiteReading {
  reachable: boolean;
  https?: boolean;
  hasViewport?: boolean;
  hasContactCta?: boolean;
  hasWhatsAppLink?: boolean;
  hasEcommerce?: boolean;
  socialProfiles?: { instagram?: string; facebook?: string; tiktok?: string; youtube?: string };
  platform?: string;
  hasMetaPixel?: boolean;
  hasAnalytics?: boolean;
  hasAdsTag?: boolean;
  hasChatWidget?: boolean;
  hasBooking?: boolean;
  copyrightYear?: number;
  cnpj?: string;
  emails?: string[];
  disallowedByRobots?: boolean;
  error?: string;
  fetchedAt?: string;
}

export type PostThemeKey = 'whatsapp' | 'prices' | 'promo' | 'menu' | 'delivery' | 'booking' | 'linkInBio' | 'giveaway';

export interface InstagramReading {
  username: string;
  found: boolean;
  isBusinessAccount?: boolean;
  businessCategory?: string;
  followers?: number;
  posts?: number;
  linkToWhatsApp?: boolean;
  linkToDelivery?: boolean;
  linkToOrdering?: boolean;
  linkToOwnSite?: boolean;
  linkToAggregator?: boolean;
  postsSummary?: {
    sample: number;
    lastPostAt?: string;
    daysSinceLastPost?: number;
    postsPerWeek?: number;
    avgLikes?: number;
    avgComments?: number;
    engagementRate?: number;
    reelsShare?: number;
    themes: Record<PostThemeKey, number>;
  };
  error?: string;
  fetchedAt: string;
}

export interface GoogleReading {
  serviceOptions: string[];
  delivery?: boolean;
  takeout?: boolean;
  dineIn?: boolean;
  reservations?: boolean;
  payments: string[];
  hoursPerWeek?: number;
  openDays?: number;
  openWeekends?: boolean;
  openLate?: boolean;
  photos?: number;
  menuUrl?: string;
  reserveUrl?: string;
  orderUrl?: string;
  priceRange?: string;
  lowRatingShare?: number;
  ownerDescription?: boolean;
  competitors: { name: string; rating?: number; reviews?: number }[];
  fetchedAt?: string;
}

export interface CompanyReading {
  cnpj: string;
  source: 'site' | 'manual';
  found: boolean;
  matches?: boolean;
  mismatchReason?: string;
  tradeName?: string;
  status?: string;
  active?: boolean;
  openedAt?: string;
  ageYears?: number;
  size?: 'ME' | 'EPP' | 'DEMAIS';
  mei?: boolean;
  simples?: boolean;
  cnae?: string;
  capital?: number;
  city?: string;
  state?: string;
  error?: string;
  fetchedAt: string;
}

export interface PageSpeedReading {
  measured: boolean;
  score?: number;
  lcpSeconds?: number;
  cls?: number;
  error?: string;
  fetchedAt: string;
}

export interface Presence {
  site?: SiteReading;
  instagram?: InstagramReading;
  google?: GoogleReading;
  company?: CompanyReading;
  pagespeed?: PageSpeedReading;
}

export interface PreQualification {
  passed: boolean;
  reasons: { code: string; message: string }[];
  notEvaluated: string[];
}

export interface QualifyResult {
  prospectId: string;
  productId: string;
  preQualification: PreQualification;
  score?: TechnicalScore;
  status: 'QUALIFIED' | 'DISQUALIFIED';
}

export const DIMENSION_LABEL: Record<string, string> = {
  signals: 'Fit com o produto',
  presence: 'Presença digital',
  reach: 'Alcance',
};

/**
 * Fato e inferência aparecem diferentes na tela por exigência da regra 1: o
 * vendedor precisa saber o que pode AFIRMAR na reunião e o que ainda é
 * suposição a confirmar com o dono.
 */
export const EVIDENCE_LABEL: Record<EvidenceKind, string> = {
  fato: 'fato',
  inferencia: 'inferência',
};

/** Informação que o vendedor recebeu sobre a empresa — não verificada. */
export interface ProspectNote {
  id: string;
  prospect_id: string;
  user_id: string | null;
  user_name: string | null;
  body: string;
  created_at: string;
}

export interface ManualLeadResult {
  prospectId: string;
  status: 'imported' | 'duplicate';
  matchedBy?: string;
  /** O nome saiu do link do Google Maps. */
  nameFromLink: boolean;
  normalizationNotes: string[];
  noteId?: string;
  jobId: string | null;
  queued: boolean;
}

/** O plano de uma coleta paga, antes de rodar (decisão 24). */
export interface CollectionPlan {
  places: number;
  requestedPlaces: number;
  capped: boolean;
  estimatedUsd: number;
  spentThisMonthUsd: number;
  monthlyBudgetUsd: number;
  remainingUsd: number;
  enabled: boolean;
  /** A estimativa inclui o add-on de contatos do Maps. */
  fullCollection: boolean;
  /** Pior caso por empresa: place, contatos e a leitura do Instagram. */
  perCompanyUsd: number;
}

// --- Diagnóstico (Fase 8) ---------------------------------------------------

export type DiagnosisKind = 'fact' | 'inference' | 'hypothesis' | 'informed';

export interface DiagnosisItem {
  text: string;
  detail?: string;
  kind: DiagnosisKind;
  origin: 'ia' | 'verificacao' | 'vendedor';
  source?: string;
}

export type PotentialLevel = 'alto' | 'medio' | 'baixo' | 'indeterminado';

export interface Diagnosis {
  status: 'pronto' | 'sem_ia' | 'ia_desatualizada' | 'sem_recomendacao' | 'descartado';
  primary?: {
    productId: string;
    productName: string;
    ticketCategory?: TicketCategory;
    technicalScore: number;
    finalScore: number | null;
    confidence: number | null;
    aiScore: number | null;
  };
  analysis?: { id: string; model: string; createdAt: string; fit?: string; stale: boolean };
  who: { summary?: string; facts: string[] };
  found: DiagnosisItem[];
  opportunities: DiagnosisItem[];
  gaps: string[];
  whyProduct?: { productName: string; reason: string; signals: DiagnosisItem[]; arguments: string[]; problems: string[] };
  approach: { openers: DiagnosisItem[]; validationQuestion?: string; avoid: string[] };
  questions: { fromAnalysis: string[]; fromProduct: string[]; toConfirm: string[] };
  secondary: { productId: string; productName: string; finalScore: number | null; reason: string }[];
  potential: { level: PotentialLevel; points: number; reasons: string[]; lowConfidence: boolean };
}

// --- Mensagens de abordagem (Fase 9) ---------------------------------------

export type OutreachStatus = 'draft' | 'approved' | 'rejected' | 'sending' | 'sent' | 'failed';

/** Que texto é este: primeira abordagem, follow-up ou resposta na conversa. */
export type OutreachKind = 'first' | 'followup' | 'reply';

export const OUTREACH_KIND_LABEL: Record<OutreachKind, string> = {
  first: 'Primeira abordagem',
  followup: 'Follow-up',
  reply: 'Resposta na conversa',
};

/** O que dá para pedir à IA agora — e, quando não dá, por quê. */
export type OutreachAvailability = Record<OutreachKind, { allowed: boolean; reason?: string }>;

export interface OutreachSplit {
  key: string;
  label: string;
  sent: number;
  replied: number;
  /** `null` quando nada foi enviado nesse grupo: 0 de 0 não é 0%. */
  rate: number | null;
}

export interface OutreachPerformance {
  total: { sent: number; replied: number; rate: number | null };
  byHook: OutreachSplit[];
  byPromptVersion: OutreachSplit[];
  byKind: OutreachSplit[];
}

export interface OutreachMessage {
  id: string;
  prospect_id: string;
  product_id: string;
  product_name: string;
  version: number;
  kind: OutreachKind;
  status: OutreachStatus;
  ai_body: string;
  body: string;
  rationale: {
    observationKey?: string;
    observation?: string;
    hookKind?: string;
    question?: string;
    why?: string;
    warnings?: string[];
  };
  instructions: string | null;
  provider: string;
  model: string;
  generated_by_name: string | null;
  edited_by_name: string | null;
  edited_at: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  rejected_by_name: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  sent_at: string | null;
  send_requested_at: string | null;
  /** A tentativa pode ter chegado: reenviar exige conferir no WhatsApp. */
  send_uncertain: boolean;
  error: string | null;
  created_at: string;
}

export const OUTREACH_STATUS_LABEL: Record<OutreachStatus, string> = {
  draft: 'Rascunho',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
  sending: 'Enviando',
  sent: 'Enviada',
  failed: 'Falhou no envio',
};

// --- WhatsApp (Fase 10) ---------------------------------------------------------

export interface WhatsAppMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  origin: 'system' | 'phone' | 'contact';
  outreach_message_id: string | null;
  body: string | null;
  message_type: string | null;
  status: 'pending' | 'server_ack' | 'delivered' | 'read' | 'received' | 'failed';
  /** Resposta automática do WhatsApp Business: está na conversa, mas não é a pessoa. */
  auto_reply?: boolean;
  occurred_at: string;
}

export interface WhatsAppOverview {
  enabled: boolean;
  configured: boolean;
  instance: string | null;
  limits: {
    dailyLimit: number;
    attemptsLast24h: number;
    replyDailyLimit?: number;
    repliesLast24h?: number;
    minIntervalSeconds: number;
    hours: string;
    /** Dias da abordagem fria ("seg–sáb"). A resposta sai em qualquer dia. */
    coldDays?: string;
    timeZone: string;
  };
  phone: { e164: string; formatted: string; mobile: boolean } | null;
  conversation: { jid: string; last_inbound_at: string | null; last_outbound_at: string | null } | null;
  messages: WhatsAppMessage[];
  suppression: { phone_e164: string; source: 'opt_out_reply' | 'manual'; reason: string; created_by_name: string | null; created_at: string } | null;
  /** O que impede uma nova abordagem agora, em texto. */
  blocker: string | null;
  sendJob: { status: 'pending' | 'running' | 'done' | 'failed'; last_error: string | null; run_at: string; updated_at: string } | null;
}

export interface WhatsAppStatus {
  configured: boolean;
  enabled: boolean;
  instance: string | null;
  state: string | null;
  error: string | null;
}

export const DELIVERY_LABEL: Record<WhatsAppMessage['status'], string> = {
  pending: 'Enviando',
  server_ack: 'Enviada',
  delivered: 'Entregue',
  read: 'Lida',
  received: 'Recebida',
  failed: 'Falhou',
};

// --- Métricas e funil (Fase 12) ----------------------------------------------

export interface FunnelStage {
  key: 'encontradas' | 'qualificadas' | 'contatadas' | 'respondidas' | 'reuniao' | 'proposta' | 'venda';
  label: string;
  count: number;
}

export interface FunnelRate {
  key: 'qualification' | 'response' | 'meeting' | 'proposal' | 'win';
  label: string;
  value: number | null;
}

export interface FunnelSummary {
  stages: FunnelStage[];
  rates: FunnelRate[];
}

export interface Opportunity {
  id: string;
  name: string;
  status: ProspectStatus;
  final_score: number | null;
  recommended_product_id: string | null;
  city: string | null;
}

// --- Fila de aprovação e caixa de respostas (decisão 47) ---------------------

/** O teto de caracteres de cada tipo — vem da API, não de uma constante na tela. */
export type MaxChars = Record<OutreachKind, number>;

export interface QueueProspect {
  id: string;
  name: string;
  city: string | null;
  category: string | null;
  final_score: number | null;
  phone_e164: string | null;
  status: string;
}

export interface DraftQueueItem {
  prospect: QueueProspect;
  message: OutreachMessage;
  send_queued: boolean;
}

export interface ReplyInboxItem {
  prospect: QueueProspect;
  last_inbound: { body: string | null; message_type: string | null; occurred_at: string };
  waiting_minutes: number;
  draft: { id: string; status: string; body: string; version: number } | null;
  generating: boolean;
}

export interface QueueSummary {
  drafts: number;
  replies: number;
}

/** O resumo da empresa ao lado do rascunho: o que conferir antes de aprovar. */
export interface ProspectBrief {
  id: string;
  name: string;
  city: string | null;
  category: string | null;
  phone: string | null;
  productName: string | null;
  finalScore: number | null;
  status: string;
  facts: string[];
  openers: string[];
  validationQuestion: string | null;
  avoid: string[];
}

export interface AiSpend {
  spent24h: number;
  dailyBudgetUsd: number;
  provider: string;
}
